import { FC, HTMLAttributes, MouseEvent, PropsWithChildren } from 'react';

import { Typography } from 'components';
import { CloseCircleIcon, LinkIcon, PdfAttachedIcon } from 'lib/svgs/Icons';
import { cn } from 'utils/cn';

interface IAttached extends PropsWithChildren<HTMLAttributes<HTMLDivElement>> {
  onMouseOver?: (e: MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (e: MouseEvent<HTMLDivElement>) => void;
  onRemove?: (tagToTemove: string) => void;
  onClick?: () => void;
  className?: string;
  filename?: string;
  link?: string;
}

const Attached: FC<IAttached> = ({
  onRemove,
  onMouseOver,
  onMouseLeave,
  onClick,
  filename,
  className,
  children,
  link
}) => {
  const extension = filename?.split('.')?.pop()?.toUpperCase() ?? '';

  const handleOnRemove = (e: MouseEvent<HTMLOrSVGElement>) => {
    e.stopPropagation();
    onRemove?.(link ? link : (children as string));
  };

  return (
    <div
      className={cn(
        'cursor-pointer select-none transition-all ease-out hover:scale-[1.001]',
        'tolq-tag flex items-center gap-3 rounded-6xl p-2',
        'w-fit rounded-3xl border border-tertiary-600 bg-quinary-900',
        link ? 'gap-2' : 'gap-3',
        className
      )}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {extension && <PdfAttachedIcon />}
      {link && <LinkIcon />}
      <div className="flex flex-col">
        {link && (
          <Typography
            variant="medium5"
            className={cn('w-fit !p-0 !leading-4 text-secondary-link capitalize-first')}
          >
            {link.replace('http://', '').replace('https://', '')}
          </Typography>
        )}
        {children && (
          <Typography
            variant="medium5"
            className={cn('w-fit break-all !p-0 !leading-4 text-tertiary-100')}
          >
            {children}
          </Typography>
        )}
        {extension && (
          <Typography
            variant="regular5"
            className={cn('w-fit break-all !p-0 !leading-3 text-tertiary-200')}
          >
            {extension}
          </Typography>
        )}
      </div>
      {!!onRemove && (
        <>
          <div className={cn('relative flex w-4 flex-col items-start justify-start')}>
            <CloseCircleIcon
              onClick={handleOnRemove}
              className={cn(
                'absolute bottom-[-8px] scale-[0.7] !text-tertiary-100  duration-75 hover:scale-[0.75]'
              )}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Attached;
